import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, SimpleChanges } from '@angular/core';
import { isDatabaseRecovering } from 'services/database-unavailable-error';
import { LoggingService } from 'services/logging.service';
import { TextDbService, TextEntry } from 'services/text-db.service';

@Component({
    selector: 'cs-text-spinner',
    templateUrl: 'cs-text-spinner.html',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CsTextSpinnerComponent implements OnDestroy {
  @Input() textsId: string = '@help:loading-hints.general';
  texts: string[] = ['Please wait...'];
  usedTexts: string[] = [];
  currentText: string = this.texts[0];
  currentTimer: ReturnType<typeof setTimeout> = null;

  constructor(
    private textDb: TextDbService,
    private logging: LoggingService,
    private cd: ChangeDetectorRef) {
    void this.loadAsync();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.textsId) {
      void this.loadAsync();
    }
  }

  async loadAsync() {
    try {
      // load the help text by that id
      let [, helpTextId] = this.textsId.match(/\@help\:(.*)/) || ['', ''];
      if (!helpTextId) {
        throw new Error(`Invalid textId '${this.textsId}'`);
      }

      let textEntry: TextEntry = await this.textDb.getTextByHelpIdAsync(helpTextId);
      if (!textEntry) {
        throw new Error(`textId '${this.textsId}' not found`);
      }

      // the text is formatted as a bullet list, split it into lines and remove the bullet formatting
      let texts = textEntry.text.split('\n');
      texts = texts.map(t => t.trim().replace(/^\s*\*\s*/, ''));
      if (texts.length === 0) {
        throw new Error(`'${this.textsId}' doesn't contain valid loading texts`);
      }

      // if all parsed correctly, start the spinner
      this.texts = texts;
      this.nextLoadingText();

    } catch (e) {
      // if a database recovery cut the read short, keep the default text: the hints are cosmetic
      // and the next spinner reads them again. Info tier, not abnormal (#6209).
      if (isDatabaseRecovering(e)) {
        this.logging.trackSilentInfo('TextSpinner: loading hints skipped while the database recovered', {
          database: e.databaseIdentifier,
        });
        return;
      }
      this.logging.trackAbnormalException(e);
    }
  }

  nextLoadingText() {
    // perform a shuffle of the texts, making sure they don't repeat until exhausted
    let potentialTexts = this.texts.filter(t => !this.usedTexts.includes(t));
    if (potentialTexts.length === 0) {
      this.usedTexts = [];
      potentialTexts = this.texts;
    }
    let nextIndex = Math.floor(Math.random() * potentialTexts.length);
    nextIndex = Math.min(nextIndex, potentialTexts.length - 1); // just in case

    let nextText = potentialTexts[nextIndex];
    this.currentText = nextText;
    this.cd.markForCheck();
    this.usedTexts.push(nextText);

    // calculate the reading time
    let charsPerSecond = 15;
    let minReadingTime = 2;
    let maxReadingTime = 5;
    let readingTime_sec = Math.max(minReadingTime, Math.min(maxReadingTime, nextText.length / charsPerSecond));

    this.currentTimer = setTimeout(() => this.nextLoadingText(), readingTime_sec * 1000);
  }

  ngOnDestroy(): void {
    this.currentText = null;
    if (this.currentTimer !== null) {
      clearTimeout(this.currentTimer);
      this.currentTimer = null;
    }
  }
}
